import {
  PersonnelImportRowActionEnum,
  PersonnelImportSupervisorMatchEnum,
  PersonnelImportSupervisorStatusEnum,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import {STATE_COLOR_SHADES} from 'src/design/tokens/colors';
import type {StateColor} from 'src/design/tokens/colors';

const ACTION_PILLS: Record<PersonnelImportRowActionEnum, PillProps> = {
  [PersonnelImportRowActionEnum.Create]: {text: 'Create', color: 'green'},
  [PersonnelImportRowActionEnum.Update]: {text: 'Update', color: 'blue'},
  [PersonnelImportRowActionEnum.Deactivate]: {
    text: 'Deactivate',
    color: 'yellow',
  },
  [PersonnelImportRowActionEnum.Skip]: {text: 'Skip', color: 'gray'},
  [PersonnelImportRowActionEnum.Reject]: {text: 'Reject', color: 'red'},
};

/**
 * Pill preset for a previewed import row action.
 */
export const ImportActionPills = (
  action: PersonnelImportRowActionEnum
): PillProps => ACTION_PILLS[action] ?? {text: 'Unknown', color: 'gray'};

/**
 * Text colour for a summary tile so it reads as the legend for the row Action pill.
 */
export const ImportActionStatColor = (
  action: PersonnelImportRowActionEnum
): string => {
  const color = ACTION_PILLS[action]?.color;
  return color && color in STATE_COLOR_SHADES
    ? STATE_COLOR_SHADES[color as StateColor]
    : 'gray.7';
};

const SUPERVISOR_PILLS: Record<PersonnelImportSupervisorStatusEnum, PillProps> =
  {
    [PersonnelImportSupervisorStatusEnum.Unchanged]: {
      text: 'Unchanged',
      color: 'gray',
    },
    [PersonnelImportSupervisorStatusEnum.Cleared]: {
      text: 'Cleared',
      color: 'yellow',
    },
    [PersonnelImportSupervisorStatusEnum.Matched]: {
      text: 'Matched',
      color: 'green',
    },
    [PersonnelImportSupervisorStatusEnum.NotFound]: {
      text: 'Not found',
      color: 'yellow',
    },
    [PersonnelImportSupervisorStatusEnum.Ambiguous]: {
      text: 'Multiple matches',
      color: 'red',
    },
    [PersonnelImportSupervisorStatusEnum.Invalid]: {
      text: 'Not valid',
      color: 'red',
    },
    [PersonnelImportSupervisorStatusEnum.VendorManager]: {
      text: 'Vendor manager',
      color: 'blue',
    },
  };

/**
 * Pill preset for the supervisor resolution status of a previewed import row.
 */
export const ImportSupervisorPills = (
  status: PersonnelImportSupervisorStatusEnum
): PillProps => SUPERVISOR_PILLS[status] ?? {text: 'Unknown', color: 'gray'};

const MATCHED_BY_LABELS: Record<PersonnelImportSupervisorMatchEnum, string> = {
  [PersonnelImportSupervisorMatchEnum.None]: '',
  [PersonnelImportSupervisorMatchEnum.FileEmployeeId]:
    'matched by employee ID in file',
  [PersonnelImportSupervisorMatchEnum.FileEmail]: 'matched by email in file',
  [PersonnelImportSupervisorMatchEnum.DatabaseEmployeeId]:
    'matched by employee ID on record',
  [PersonnelImportSupervisorMatchEnum.DatabaseEmail]:
    'matched by email on record',
  [PersonnelImportSupervisorMatchEnum.VendorManager]:
    'matched by vendor manager on record',
};

/**
 * Dimmed caption describing which key resolved a row's supervisor.
 */
export const ImportSupervisorMatchLabel = (
  matchedBy: PersonnelImportSupervisorMatchEnum
): string => MATCHED_BY_LABELS[matchedBy] ?? '';
